import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useApi } from "context/ApiContext";
import { useStore } from "context/StoreContext";
import CookieBanner from "./CookieBanner";
import Show from "./Show";
import { ChatWidget } from "./ChatWidget";
import AdminSidebar from "./AdminSidebar";
import Footer from "./Footer";
import LanguageSwitcher from "./LanguageSwitcher";
import { useCart } from "context/CartContext";
import { useAdminUnreadMessages } from "../hooks/useAdminUnreadMessages";
import { SetupProgressHeader } from "./admin/SetupProgressHeader";
import { Spinner } from "./ui/Spinner";
import { useCartAnimationContext } from "../context/CartAnimationContext";
import { 
 FaShoppingBag, 
 FaCog, 
 FaShoppingCart, 
 FaUser, 
 FaSignOutAlt, 
 FaClipboardList,
 FaBars,
 FaTimes,
 FaSearch,
 FaFire,
 FaStar,
 FaMapMarkerAlt,
 FaChevronDown,
 FaComments
} from "react-icons/fa";
import { 
 FaUserCheck,
 FaUserXmark
} from "react-icons/fa6";
import { createPortal } from "react-dom";
import { getLogoUrl } from "utils/logoUtils";

// Products Dropdown Component
const ProductsDropdown: React.FC<{ 
 isOpen: boolean; 
 onClose: () => void;
 position: { top: number; left: number; width: number };
}> = ({ isOpen, onClose, position }) => {
 const { api } = useApi();
 const { t } = useTranslation();
 const [categories, setCategories] = useState<any[]>([]);
 const [featuredProducts, setFeaturedProducts] = useState<any[]>([]);
 const [loading, setLoading] = useState(true);
 const [dataFetched, setDataFetched] = useState(false);
 const dropdownRef = useRef<HTMLDivElement>(null);
 const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

 const fetchData = useCallback(async () => {
  // Only fetch if data hasn't been fetched yet
  if (dataFetched) return;
  
  try {
   setLoading(true);
   const [categoriesRes, productsRes] = await Promise.all([
    api.get("/categories"),
    api.get("/products?limit=4&status=active&sort=newest"),
   ]);
   // Get first 8 categories (flatten if needed)
   const allCategories = categoriesRes.data.categories || [];
   const flattenCategories = (cats: any[]): any[] => {
    const result: any[] = [];
    cats.forEach((cat) => {
     result.push(cat);
     if (cat.children && cat.children.length > 0) {
      result.push(...flattenCategories(cat.children));
     }
    });
    return result;
   };
   const flatCategories = flattenCategories(allCategories);
   setCategories(flatCategories.slice(0, 8));
   setFeaturedProducts(productsRes.data.products || []);
   setDataFetched(true);
  } catch (error) {
   console.error("Error fetching dropdown data:", error);
   setCategories([]);
   setFeaturedProducts([]);
  } finally {
   setLoading(false);
  }
 }, [api, dataFetched]);

 useEffect(() => {
  if (!isOpen) return;
  fetchData();
 }, [isOpen, fetchData]);

 useEffect(() => {
  if (!isOpen) return;

  const handleClickOutside = (event: MouseEvent) => {
   if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
    // Check if click is on the Products link
    const target = event.target as HTMLElement;
    const productsLink = target.closest('a[href="/products"]');
    if (!productsLink) {
     onClose();
    }
   }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
 }, [isOpen, onClose]);

 const handleMouseEnter = () => {
  if (closeTimeoutRef.current) {
   clearTimeout(closeTimeoutRef.current);
   closeTimeoutRef.current = null;
  }
 };

 const handleMouseLeave = () => {
  closeTimeoutRef.current = setTimeout(() => {
   onClose();
  }, 200); // Small delay to allow moving to dropdown
 };

 // Cleanup timeout on unmount
 useEffect(() => {
  return () => {
   if (closeTimeoutRef.current) {
    clearTimeout(closeTimeoutRef.current);
   }
  };
 }, []);

 if (!isOpen) {
  return null;
 }

 const dropdownContent = (
  <div
   ref={dropdownRef}
   className="fixed bg-white border border-store-accent-light  z-50"
   style={{
    top: `${position.top}px`,
    left: `${position.left}px`,
    width: `${Math.max(position.width, 600)}px`,
    maxHeight: "80vh",
    overflowY: "auto",
    marginTop: "4px", // Small gap to connect visually
   }}
   onMouseEnter={handleMouseEnter}
   onMouseLeave={handleMouseLeave}
  >
   <div className="p-6 grid grid-cols-3 gap-6">
    {/* Categories Section */}
    <div>
     <h3 className="font-semibold text-store-primary mb-3 text-sm uppercase tracking-wide">
      {t("home.categories")}
     </h3>
     <ul className="space-y-2">
      {loading ? (
       <li className="text-sm text-store-text">Loading...</li>
      ) : categories.length > 0 ? (
       categories.map((category) => (
        <li key={category._id}>
         <Link
          to={`/products?category=${category.slug || category._id}`}
          className="text-sm text-store-primary hover:text-store-secondary transition-colors block py-1"
          onClick={onClose}
         >
          {category.name}
         </Link>
        </li>
       ))
      ) : (
       <li className="text-sm text-store-text">No categories</li>
      )}
     </ul>
    </div>

    {/* Promotions Section */}
    <div>
     <h3 className="font-semibold text-store-primary mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
      <FaFire className="text-store-accent" />
      Promotions
     </h3>
     <ul className="space-y-2">
      <li>
       <Link
        to="/products?sort=price-asc"
        className="text-sm text-store-primary hover:text-store-secondary transition-colors block py-1"
        onClick={onClose}
       >
        Best Deals
       </Link>
      </li>
      <li>
       <Link
        to="/products?badge=sale"
        className="text-sm text-store-primary hover:text-store-secondary transition-colors block py-1"
        onClick={onClose}
       >
        On Sale
       </Link>
      </li>
      <li>
       <Link
        to="/products?sort=newest"
        className="text-sm text-store-primary hover:text-store-secondary transition-colors block py-1"
        onClick={onClose}
       >
        New Arrivals
       </Link>
      </li>
     </ul>
    </div>

    {/* Featured Products Section */}
    <div>
     <h3 className="font-semibold text-store-primary mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
      <FaStar className="text-store-accent" />
      Featured
     </h3>
     <ul className="space-y-2">
      {loading ? (
       <li className="text-sm text-store-text">Loading...</li>
      ) : featuredProducts.length > 0 ? (
       featuredProducts.map((product) => (
        <li key={product._id || `product-${Math.random()}`}>
         <Link
          to={`/products/${product.handle || product._id}`}
          className="text-sm text-store-primary hover:text-store-secondary transition-colors block py-1 line-clamp-1"
          onClick={onClose}
         >
          {product.title || "Untitled Product"}
         </Link>
        </li>
       ))
      ) : (
       <li className="text-sm text-store-text">
        {loading ? "Loading..." : "No featured products"}
       </li>
      )}
     </ul>
    </div>
   </div>
  </div>
 );

 return createPortal(dropdownContent, document.body);
};


const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
 const { userInfo: user, logout, user: userLogin } = useAuth();
 const { t } = useTranslation();
 const { maintenanceMode } = useApi();
 const { i18n } = useTranslation();
 const { itemCount } = useCart();
 const { store, isPluginEnabled, loading: storeLoading } = useStore();
 const location = useLocation();
 const navigate = useNavigate();
 const unreadCount = useAdminUnreadMessages();
 const { registerCartIcon } = useCartAnimationContext();
 const LogoComponent = maintenanceMode ? "span" : Link;

 // Register cart icon for animation using callback ref
 const cartIconRefCallback = useCallback((element: HTMLAnchorElement | null) => {
  if (element) {
   registerCartIcon(element);
  }
 }, [registerCartIcon]);

 // Show loading state while store is being fetched (but still render children for route matching)
 // Show error message if store doesn't exist (after loading is complete)
 // Only show this on subdomain (store routes), not on main domain
 const showStoreError = !storeLoading && !store;
 const showStoreLoading = storeLoading;

 // Get store branding - use API endpoint for serving files
 const storeLogo = getLogoUrl(store?.logo, "/logo512.png");
 const themePalette = useMemo(() => store?.themePalette || {}, [store?.themePalette]);
 // Theme colors are preloaded server-side as CSS variables
 // Only extract values needed for inline styles (not for setting CSS variables)
 const backgroundColor = themePalette.background || "#F9FAFB";
 const textColor = themePalette.text || "#6B7280";
 const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
 const [userMenuOpen, setUserMenuOpen] = useState(false);
 const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
 const [productsDropdownPosition, setProductsDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
 const [searchOpen, setSearchOpen] = useState(false);
 const [searchQuery, setSearchQuery] = useState("");
 const [isSearchFocused, setIsSearchFocused] = useState(false);
 const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
  // Start collapsed on mobile, expanded on desktop
  if (typeof window !== "undefined") {
   return window.innerWidth < 1024;
  }
  return false;
 });
 const productsLinkRef = useRef<HTMLAnchorElement>(null);
 const searchRef = useRef<HTMLInputElement>(null);
 const userMenuButtonRef = useRef<HTMLButtonElement>(null);
 const userMenuRef = useRef<HTMLDivElement>(null);

 const isHomePage = location.pathname === "/";
 const isProductDetailPage = location.pathname.startsWith("/products/");
 const isProductListPage = location.pathname === "/products";
 const isCheckoutPage = location.pathname === "/checkout";
 const isProfilePage = location.pathname === "/profile";
 const isAddressesPage = location.pathname === "/addresses";
 const isOrdersPage = location.pathname === "/orders";
 const isAdminPage = window.location.pathname.startsWith("/admin");
 const showSidebar = isAdminPage && (userLogin?.role === "admin" || userLogin?.role === "store_admin" || userLogin?.role === "co_admin");

 // Close mobile menu when navigating to admin pages
 useEffect(() => {
  if (isAdminPage && mobileMenuOpen) {
   setMobileMenuOpen(false);
  }
 }, [isAdminPage, mobileMenuOpen]);

 useEffect(() => {
  const savedLanguage = localStorage.getItem("language");
  if (savedLanguage) {
   i18n.changeLanguage(savedLanguage);
  }
 }, [i18n]);

 useEffect(() => {
  return () => {
   if (productsCloseTimeoutRef.current) {
    clearTimeout(productsCloseTimeoutRef.current);
   }
  };
 }, []);

 const wasSearchFocusedRef = useRef(false);

 useEffect(() => {
  if (searchOpen && searchRef.current) {
   searchRef.current.focus();
   wasSearchFocusedRef.current = true;
  }
 }, [searchOpen]);

 // Maintain focus during typing
 useEffect(() => {
  if (wasSearchFocusedRef.current && searchRef.current && document.activeElement !== searchRef.current && searchOpen) {
   const cursorPosition = searchRef.current.selectionStart || searchQuery.length;
   searchRef.current.focus();
   searchRef.current.setSelectionRange(cursorPosition, cursorPosition);
  }
 }, [searchQuery, searchOpen]);

 const productsCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

 const handleProductsMouseEnter = () => {
  if (productsCloseTimeoutRef.current) {
   clearTimeout(productsCloseTimeoutRef.current);
   productsCloseTimeoutRef.current = null;
  }
  if (productsLinkRef.current) {
   const rect = productsLinkRef.current.getBoundingClientRect();
   setProductsDropdownPosition({
    top: rect.bottom + 4,
    left: rect.left,
    width: rect.width,
   });
   setProductsDropdownOpen(true);
  }
 };

 const handleProductsMouseLeave = () => {
  // Add delay before closing to allow moving to dropdown
  productsCloseTimeoutRef.current = setTimeout(() => {
   setProductsDropdownOpen(false);
  }, 200);
 };

 const handleSearchSubmit = (e: React.FormEvent) => {
  e.preventDefault();
  if (searchQuery.trim()) {
   navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
   setSearchOpen(false);
   setSearchQuery("");
  }
 };

 const toggleSidebar = () => {
  setSidebarCollapsed(!sidebarCollapsed);
 };

 // Handle click outside user menu
 useEffect(() => {
  if (!userMenuOpen) return;

  const handleClickOutside = (event: MouseEvent) => {
   if (
    userMenuRef.current &&
    !userMenuRef.current.contains(event.target as Node) &&
    userMenuButtonRef.current &&
    !userMenuButtonRef.current.contains(event.target as Node)
   ) {
    setUserMenuOpen(false);
   }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
 }, [userMenuOpen]);

 // Theme is preloaded server-side as CSS variables, no need to set them here

 // Set store logo as favicon for browser tab
 const faviconUrl = store?.logo ? getLogoUrl(store.logo, "/favicon.ico") : "/favicon.ico";

 return (
  <div
   className={`min-h-screen flex flex-col overflow-x-hidden font-sans ${isAdminPage ? "admin-page" : ""}`}
   style={{
    background: isAdminPage
     ? "#F9FAFB"
     : themePalette.background 
       ? `linear-gradient(135deg, ${backgroundColor} 0%, ${backgroundColor}dd 100%)`
       : "linear-gradient(135deg, #F5F1ED 0%, #EBE5DE 100%)",
    color: isAdminPage ? "#111827" : textColor,
    // Font family is controlled at Tailwind level via fontFamily.sans override
   }}
  >
   <Helmet>
    <link rel="icon" href={faviconUrl} />
    <link rel="apple-touch-icon" href={faviconUrl} />
   </Helmet>
   <CookieBanner />

   {/* Enlarged Header */}
   <header 
    className={`sticky top-0 z-50 overflow-x-hidden border-b bg-white ${
      isAdminPage ? "border-gray-200" : "border-store-accent-light"
    }`}
   >
    <div className="px-4 md:px-8 py-4 flex items-center justify-between relative">
     {/* Left: Sidebar Toggle (Admin pages only) */}
     <div className="flex items-center flex-shrink-0 z-10 gap-4">

     <Show ifcond={showSidebar}>
      <div className="flex items-center flex-shrink-0 z-10">
      {/* Sidebar Toggle Button - Mobile & Desktop Admin */}
        <button
         onClick={toggleSidebar}
         className={`lg:hidden p-2 transition-colors ${
          isAdminPage 
            ? "hover:text-gray-600 text-gray-700" 
            : "hover:text-store-secondary text-store-primary"
         }`}
         aria-label="Toggle sidebar"
        >
         {sidebarCollapsed ? (
          <FaBars className="text-xl" />
         ) : (
          <FaTimes className="text-xl" />
         )}
        </button>
        <button
         onClick={toggleSidebar}
         className={`hidden lg:block p-2 transition-colors ${
          isAdminPage 
            ? "hover:text-gray-600 text-gray-700" 
            : "hover:text-store-secondary text-store-primary"
         }`}
         aria-label="Toggle sidebar"
        >
         {sidebarCollapsed ? (
          <FaBars className="text-xl" />
         ) : (
          <FaTimes className="text-xl" />
         )}
        </button>
      </div>
     </Show>
     {/* Mobile Menu Toggle - Only show on non-admin pages, on the left side */}
     {!isAdminPage && (
      <button
       onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
       className="lg:hidden p-2 hover:text-store-secondary transition-colors text-store-primary relative w-8 h-8 flex items-center justify-center"
       aria-label="Toggle mobile menu"
      >
       <FaBars
        className={`text-xl absolute transition-all duration-300 ease-in-out ${
         mobileMenuOpen
          ? "opacity-0 rotate-90 scale-0"
          : "opacity-100 rotate-0 scale-100"
        }`}
       />
       <FaTimes
        className={`text-xl absolute transition-all duration-300 ease-in-out ${
         mobileMenuOpen
          ? "opacity-100 rotate-0 scale-100"
          : "opacity-0 -rotate-90 scale-0"
        }`}
       />
      </button>
     )}
     {/* Center: Logo - Absolutely centered on mobile, left-aligned on desktop - Hidden on admin pages */}
     {!isAdminPage && (
      <LogoComponent
       to={maintenanceMode ? "" : "/"}
       className={`absolute left-1/2 transform -translate-x-1/2 lg:relative lg:left-0 lg:transform-none flex items-center flex-shrink-0 font-store z-0 transition-opacity duration-200 ${
        isSearchFocused ? "lg:opacity-100 opacity-0 pointer-events-none" : "opacity-100"
       }`}
      >
       <img
        className="h-12 w-auto drop-shadow"
        src={storeLogo}
        alt={store?.name || t("home")}
        onError={(e) => {
         // Fallback to default logo if store logo fails to load
         (e.target as HTMLImageElement).src = "/logo512.png";
        }}
       />
      </LogoComponent>
     )}
     </div>
     
     {/* Spacer for symmetry on mobile - matches left sidebar toggle or hamburger menu width */}
     <div className={`flex-shrink-0 lg:hidden ${showSidebar ? "w-10" : !isAdminPage ? "w-10" : "w-0"}`} />
     
     {/* Desktop Navigation - Only show when not admin page */}
     {!maintenanceMode && !isAdminPage && (
      <nav className="hidden lg:flex items-center gap-6 ml-8 flex-shrink-0">
        <div
         className="relative"
         onMouseEnter={handleProductsMouseEnter}
         onMouseLeave={handleProductsMouseLeave}
        >
         <NavLink
          ref={productsLinkRef}
          to="/products"
          className={({ isActive }) =>
           `relative text-base font-medium transition-all duration-300 ${
            isActive
             ? "text-store-primary"
             : "text-store-primary hover:text-store-secondary"
           }`
          }
         >
          {({ isActive }) => (
           <>
            <span className="flex items-center gap-2">
             <FaShoppingBag className="text-base" />
             <span>{t("menu.products")}</span>
            </span>
            {isActive && (
             <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-store-accent-light transform origin-left transition-all duration-300" />
            )}
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-store-accent-light transform scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100" />
           </>
          )}
         </NavLink>
         <ProductsDropdown
          isOpen={productsDropdownOpen}
          onClose={() => setProductsDropdownOpen(false)}
          position={productsDropdownPosition}
         />
        </div>
        <Show ifcond={!!userLogin && (userLogin?.role === "admin" || userLogin?.role === "store_admin" || userLogin?.role === "co_admin")}>
         <div className="w-px h-6 bg-store-accent-light mx-1"></div>
         <NavLink
          to="/admin/dashboard"
          className={({ isActive }) =>
           `relative text-base font-medium transition-all duration-300 ${
            isActive
             ? "text-store-primary"
             : "text-store-primary hover:text-store-secondary"
           }`
          }
         >
          {({ isActive }) => (
           <>
            <span className="flex items-center gap-2">
             <FaCog className="text-base" />
             <span>{t("menu.admin")}</span>
            </span>
            {isActive && (
             <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-store-accent-light transform origin-left transition-all duration-300" />
            )}
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-store-accent-light transform scale-x-0 origin-left transition-transform duration-300 hover:scale-x-100" />
           </>
          )}
         </NavLink>
        </Show>
       </nav>
      )}

     {/* Right: Tools & User Menu */}
     <div className="flex items-center gap-4 flex-shrink-0">
      {/* Global Search */}
      {!maintenanceMode && !isAdminPage && (
       <div className="relative flex items-center">
        <form
         onSubmit={handleSearchSubmit}
         className={`flex items-center gap-2 bg-white border border-store-accent-light px-3 py-2 transition-all duration-300 ease-in-out overflow-hidden ${
          searchOpen
           ? "w-48 md:w-64 opacity-100"
           : "w-0 opacity-0 pointer-events-none"
         }`}
         onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
           setSearchOpen(false);
          }
         }}
        >
         <FaSearch className="text-store-primary-light flex-shrink-0" />
         <input
          ref={searchRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => {
           wasSearchFocusedRef.current = true;
           setIsSearchFocused(true);
          }}
          onBlur={() => {
           setTimeout(() => {
            if (document.activeElement !== searchRef.current) {
             wasSearchFocusedRef.current = false;
             setIsSearchFocused(false);
            }
           }, 0);
          }}
          placeholder={t("products.searchPlaceholder")}
          className="outline-none text-sm flex-1 min-w-0"
         />
         <button
          type="button"
          onClick={() => {
           setSearchOpen(false);
           setSearchQuery("");
           setIsSearchFocused(false);
          }}
          className="text-store-primary-light hover:text-store-primary flex-shrink-0"
         >
          <FaTimes className="text-sm" />
         </button>
        </form>
        <button
         onClick={() => setSearchOpen(true)}
         className={`p-2 text-store-primary hover:text-store-secondary transition-all duration-300 ease-in-out ${
          searchOpen
           ? "w-0 opacity-0 pointer-events-none overflow-hidden"
           : "w-auto opacity-100"
         }`}
         aria-label="Search"
        >
         <FaSearch className="text-xl" />
        </button>
       </div>
      )}

      {/* Cart Icon */}
      {!maintenanceMode && !isAdminPage && (
       <Link
        ref={cartIconRefCallback}
        to="/cart"
        className="relative flex items-center justify-center p-2 hover:text-store-secondary transition-colors text-store-primary"
       >
        <FaShoppingCart className="text-xl" />
        {itemCount > 0 && (
         <span className="absolute top-0 right-0 bg-store-secondary text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
          {itemCount > 99 ? "99+" : itemCount}
         </span>
        )}
       </Link>
      )}

      {/* Setup Progress Indicator - Admin pages only */}
      {isAdminPage && (
       <SetupProgressHeader />
      )}

      {/* User Menu / Auth */}
      {!maintenanceMode && (
       <div className="relative">
        {user ? (
         <div className="flex items-center gap-3">
          <button
           ref={userMenuButtonRef}
           onClick={(e) => {
            e.stopPropagation();
            setUserMenuOpen(!userMenuOpen);
           }}
           onTouchStart={(e) => {
            e.stopPropagation();
           }}
           className="relative flex items-center gap-2 px-3 py-2 hover:text-store-secondary transition-colors text-store-primary touch-manipulation"
           style={{ WebkitTapHighlightColor: "transparent" }}
          >
           <div className="relative">
            {(user as any)?.user?.isVerified !== undefined ? (
             (user as any)?.user?.isVerified ? (
              <FaUserCheck className="text-lg flex-shrink-0 text-green-600" title="Email verified" />
             ) : (
              <FaUserXmark className="text-lg flex-shrink-0 text-gray-400" title="Email not verified" />
             )
            ) : (
             <FaUser className="text-lg flex-shrink-0" />
            )}
            {unreadCount > 0 && (
             <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold min-w-[18px] h-4.5 px-1 flex items-center justify-center">
              {unreadCount > 99 ? "99+" : unreadCount}
             </span>
            )}
           </div>
           <span className="hidden sm:inline text-sm font-medium truncate max-w-[120px]">
            {(user as any)?.user?.name?.split(" ")[0]}
           </span>
           <FaChevronDown
            className={`text-store-accent flex-shrink-0 text-xs transition-transform duration-200 ${
             userMenuOpen ? "rotate-180" : "rotate-0"
            }`}
           />
          </button>
         </div>
        ) : (
         <Link
          to="/login"
          className="px-4 py-2 bg-store-primary text-white text-sm font-medium hover:bg-store-primary-dark transition-all"
         >
          {t("login")}
         </Link>
        )}
       </div>
      )}

      {/* User Dropdown Menu - Portal */}
      {userMenuOpen && userMenuButtonRef.current && user && createPortal(
       <div
        ref={userMenuRef}
        className="fixed bg-white  border border-store-accent-light py-2 z-[100]"
        style={{
         top: `${userMenuButtonRef.current.getBoundingClientRect().bottom + 8}px`,
         right: `${Math.max(8, window.innerWidth - userMenuButtonRef.current.getBoundingClientRect().right)}px`,
         width: "192px", // w-48 = 192px
         maxWidth: `${Math.min(192, window.innerWidth - 16)}px`, // Ensure it doesn't overflow on mobile
         animation: "fadeInSlideDown 0.2s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
       >
        <div className="px-4 py-2 border-b border-store-accent-light bg-store-background">
         <p className="text-xs text-store-primary-light">
          Logged in as
         </p>
         <p className="text-sm font-semibold text-store-primary">
          {(user as any)?.user?.name}
         </p>
         <p className="text-xs text-store-primary-light">
          {(user as any)?.user?.email}
         </p>
        </div>
        <Link
         to="/profile"
         className="block px-4 py-2 text-sm text-store-primary hover:bg-store-secondary hover:bg-opacity-30 transition-all"
         onClick={() => setUserMenuOpen(false)}
        >
         <FaUser className="inline mr-2" /> {t("menu.profile")}
        </Link>
        <Link
         to="/addresses"
         className="block px-4 py-2 text-sm text-store-primary hover:bg-store-secondary hover:bg-opacity-30 transition-all"
         onClick={() => setUserMenuOpen(false)}
        >
         <FaMapMarkerAlt className="inline mr-2" /> Addresses
        </Link>
        <Link
         to="/orders"
         className="block px-4 py-2 text-sm text-store-primary hover:bg-store-secondary hover:bg-opacity-30 transition-all"
         onClick={() => setUserMenuOpen(false)}
        >
         <FaClipboardList className="inline mr-2" /> {t("menu.orders")}
        </Link>
        <Show
         ifcond={!!userLogin && userLogin?.role === "admin"}
        >
         <Link
          to="/admin/dashboard"
          className="block px-4 py-2 text-sm text-store-primary-dark hover:bg-store-secondary hover:bg-opacity-30 transition-all"
          onClick={() => setUserMenuOpen(false)}
         >
          <FaCog className="inline mr-2" /> {t("menu.admin")}
         </Link>
        </Show>
        <Show
         ifcond={!!userLogin && (userLogin?.role === "admin" || userLogin?.role === "store_admin" || userLogin?.role === "co_admin")}
        >
         <Link
          to="/admin/helpdesk"
          className="block px-4 py-2 text-sm text-store-primary hover:bg-store-accent-light hover:bg-opacity-30 transition-all flex items-center justify-between"
          onClick={() => setUserMenuOpen(false)}
         >
          <span>
           <FaComments className="inline mr-2" /> {t("admin.sidebar.support")}
          </span>
          {unreadCount > 0 && (
           <span className="bg-red-500 text-white text-xs font-bold min-w-[18px] h-4.5 px-1 flex items-center justify-center">
            {unreadCount > 99 ? "99+" : unreadCount}
           </span>
          )}
         </Link>
        </Show>
        <button
         onClick={() => {
          logout(true);
          setUserMenuOpen(false);
         }}
         className="w-full text-left px-4 py-2 text-sm text-store-primary-dark hover:bg-store-background transition-all border-t border-store-accent-light"
        >
         <FaSignOutAlt className="inline mr-2" /> {t("logout")}
        </button>
       </div>,
       document.body
      )}
     </div>
    </div>

    {/* Mobile Navigation Menu */}
    {!maintenanceMode && !isAdminPage && (
     <div
      className={`lg:hidden border-t border-store-accent-light bg-store-background overflow-hidden transition-all duration-300 ease-in-out ${
       mobileMenuOpen
        ? "max-h-[500px] opacity-100"
        : "max-h-0 opacity-0"
      }`}
     >
      <div className={`px-4 py-3 space-y-2 transition-opacity duration-300 ${
       mobileMenuOpen ? "opacity-100 delay-75" : "opacity-0"
      }`}>
       <NavLink
        to="/products"
        className={({ isActive }) =>
         `flex items-center gap-2 px-3 py-2 text-sm font-medium transition-all block ${
          isActive
           ? "text-store-primary border-b-2 border-store-secondary"
           : "text-store-primary hover:text-store-secondary"
         }`
        }
        onClick={() => setMobileMenuOpen(false)}
       >
        <FaShoppingBag className="inline mr-2" /> {t("menu.products")}
       </NavLink>
      <Show ifcond={!!userLogin && (userLogin?.role === "admin" || userLogin?.role === "store_admin")}>
       <NavLink
        to="/admin/dashboard"
        className={({ isActive }) =>
         `flex items-center gap-2 px-3 py-2 text-sm font-medium transition-all block ${
          isActive
           ? "text-store-primary border-b-2 border-store-secondary"
           : "text-store-primary hover:text-store-secondary"
         }`
        }
        onClick={() => setMobileMenuOpen(false)}
       >
        <FaCog className="inline mr-2" /> {t("menu.admin")}
       </NavLink>
      </Show>
      </div>
     </div>
    )}
   </header>
   <div className="flex flex-1 relative overflow-x-hidden">
    {showSidebar && (
     <AdminSidebar 
      isCollapsed={sidebarCollapsed} 
      onToggle={toggleSidebar}
      storeLogo={storeLogo}
      storeName={store?.name}
     />
    )}
    <main
     className={`flex-grow overflow-x-hidden transition-all duration-300 ${
      showSidebar
       ? sidebarCollapsed
         ? "lg:ml-20"
         : "lg:ml-64"
       : ""
     } ${
      isAdminPage
       ? "mx-2 p-4 md:p-6 bg-white mt-2 mb-2 border-gray-200"
       : isProductDetailPage || isProductListPage || isCheckoutPage || isProfilePage || isAddressesPage || isOrdersPage || isHomePage
       ? ""
       : "mx-auto p-6 md:p-10 bg-white mb-4"
     }`}
    >
     {showStoreLoading ? (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
       <div className="text-center">
        <Spinner size="lg" className="mx-auto mb-4" />
        <p className="text-gray-600">Loading store...</p>
       </div>
      </div>
     ) : showStoreError ? (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
       <div className="text-center p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Store Not Found</h1>
        <p className="text-gray-600 mb-4">
         The store you're looking for doesn't exist or is not available.
        </p>
        <p className="text-sm text-gray-500">
         Please check the URL or contact support if you believe this is an error.
        </p>
       </div>
      </div>
     ) : (
      children
     )}
    </main>
   </div>
   {!maintenanceMode && isPluginEnabled("help-desk") && userLogin && userLogin?.role !== "store_admin" && userLogin?.role !== "co_admin" && <ChatWidget />}
   {!maintenanceMode && !isAdminPage && <Footer />}
   {!maintenanceMode && isAdminPage && (
    <div className="bg-white border-t border-gray-200 p-4">
     <div className="max-w-7xl mx-auto flex justify-end">
      <LanguageSwitcher />
     </div>
    </div>
   )}
  </div>
 );
};

export default Layout;
